<script setup lang="ts">
import { CalendarCheck, User, Users, Calendar } from 'lucide-vue-next'

interface NextSession {
  date: string
  time: string | null
  subject: string
  type: string
}

interface ScheduleStatsData {
  sessionsThisMonth: number
  oneOnOneSessions: number
  groupSessions: number
  totalHours: number
  nextSession: NextSession | null
  periodLabel?: string
}

defineProps<{
  stats: ScheduleStatsData
}>()
</script>

<template>
  <section class="grid grid-cols-2 md:grid-cols-4 gap-4">
    <!-- Sessions This Month -->
    <div class="bg-white rounded-xl p-5 border border-gray-200">
      <div class="flex items-center justify-between mb-3">
        <div class="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
          <CalendarCheck class="w-5 h-5 text-blue-600" />
        </div>
      </div>
      <h3 class="text-3xl font-bold text-gray-900">{{ stats.sessionsThisMonth }}</h3>
      <p class="text-gray-500 text-sm">{{ stats.periodLabel || 'Sessions This Month' }}</p>
      <p class="text-gray-400 text-xs mt-0.5">{{ stats.totalHours }}h total</p>
    </div>

    <!-- 1:1 Sessions -->
    <div class="bg-white rounded-xl p-5 border border-gray-200">
      <div class="flex items-center justify-between mb-3">
        <div class="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
          <User class="w-5 h-5 text-blue-600" />
        </div>
      </div>
      <h3 class="text-3xl font-bold text-gray-900">{{ stats.oneOnOneSessions }}</h3>
      <p class="text-gray-500 text-sm">1:1 Sessions</p>
    </div>

    <!-- Group Sessions -->
    <div class="bg-white rounded-xl p-5 border border-gray-200">
      <div class="flex items-center justify-between mb-3">
        <div class="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
          <Users class="w-5 h-5 text-purple-600" />
        </div>
      </div>
      <h3 class="text-3xl font-bold text-gray-900">{{ stats.groupSessions }}</h3>
      <p class="text-gray-500 text-sm">Group Sessions</p>
    </div>

    <!-- Next Session -->
    <div class="bg-white rounded-xl p-5 border border-gray-200">
      <div class="flex items-center justify-between mb-3">
        <div class="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
          <Calendar class="w-5 h-5 text-green-600" />
        </div>
      </div>
      <template v-if="stats.nextSession">
        <h3 class="text-lg font-bold text-gray-900">{{ stats.nextSession.date }}</h3>
        <p class="text-gray-500 text-sm">
          {{ stats.nextSession.time ? `${stats.nextSession.time} · ` : '' }}{{ stats.nextSession.subject }}
        </p>
      </template>
      <template v-else>
        <h3 class="text-2xl font-bold text-gray-900">--</h3>
        <p class="text-gray-500 text-sm">No upcoming sessions</p>
      </template>
    </div>
  </section>
</template>
